import { empty, strval, strtoupper, trim, is_numeric, intval, truthy } from '../../php-compat';
import type { PortEnv, PhpRef } from '../../port-contract';
import { at, array, isArray, values, num, f, put } from './support-b2';
export interface Deps {
    item_field(row: unknown, key: string): unknown;
    layer_value(row: unknown, key: string): unknown;
    ft_spell_is_level_upcastable(row: unknown): boolean;
    janus_validation_veto(reason: string): never;
    ft_painted_wall_geometry(raw: unknown): unknown;
    ft_painted_wall_length(wall: unknown): unknown;
    is_target_encounter_format(token: string): boolean;
    parse_encounter_target_string(token: string): unknown;
}
/** PHP: ft_painted_wall_attachment (includes/functions/janus/encounter/painted_wall_cast.php:6) */
export function ftPaintedWallAttachment(id: string): string | null {
    id = strtoupper(id);
    if (id === 'FB72381D-4541-0398-CD0B-9F6A7F997D98')
        return 'floating';
    return ['B9032A2F-B68E-0D7E-A234-90CAD8959892', '30DF4F8A-BA72-91D3-74F6-A26FA3A54B9B', 'D7049DF3-2800-E170-B3B3-E5AB57CD7C9F', '3375068E-F4FB-C567-C7D4-D2D4B861CD10', '98E982F2-46EF-1B81-87D2-6B0E8AFDE922', 'E10D3A9E-D524-C1E3-7745-99D69403CE63', 'C88CB529-C244-07ED-92F4-260FCAC62992', 'DA53F59D-2714-47C7-A973-C68516B817D2', 'FC95B5FB-B47D-7848-F956-06663487666C', '2999073F-2DDA-E9D5-64F2-07B7F1E3105F'].includes(id) ? 'surface' : null;
}
/** PHP: ft_apply_painted_wall_upcast_dimensions (includes/functions/janus/encounter/painted_wall_cast.php:16) */
export function ftApplyPaintedWallUpcastDimensions(ability: PhpRef<unknown>, cast: unknown, env: PortEnv<Deps>): null {
    const d = env.deps;
    if (!ftPaintedWallAttachment(strval(d.item_field(ability.value, 'item_id'))) || !empty(at(ability.value, 'computed', 'ft_wall_upcast_applied')) || !d.ft_spell_is_level_upcastable(ability.value) || !is_numeric(cast))
        return null;
    const level = num(cast);
    if (!Number.isFinite(level) || level !== Math.floor(level))
        d.janus_validation_veto('Choose a whole spell level for the painted wall.');
    const base = Number(intval(d.layer_value(ability.value, 'spell_level') ?? d.layer_value(ability.value, 'menta_level') ?? 0)), extra = Math.max(0, level - base), growth = Math.max(0, num(d.layer_value(ability.value, 'spell_upcast_increase_aoe_ft') ?? 0));
    ability.value = array(ability.value);
    put(ability.value, ['computed', 'ft_wall_upcast_applied'], 1);
    if (extra > 0 && growth > 0)
        put(ability.value, ['computed', 'aoe_distance'], f(num(d.layer_value(ability.value, 'aoe_distance')) + extra * growth));
    return null;
}
/** PHP: ft_painted_wall_for_cast (includes/functions/janus/encounter/painted_wall_cast.php:30) */
export function ftPaintedWallForCast(selection: string, caster: string, ability: unknown,
    env: Pick<PortEnv<Omit<Deps, 'ft_spell_is_level_upcastable'>>, 'globals' | 'deps'>): unknown {
    const wire = env.globals.ft_painted_wall_wire, d = env.deps;
    if (!isArray(wire))
        return null;
    if (strtoupper(strval(at(wire, 'casterId') ?? '')) !== strtoupper(caster) || strtoupper(strval(at(wire, 'abilityId') ?? '')) !== strtoupper(strval(d.item_field(ability, 'item_id'))) || strtoupper(trim(strval(at(wire, 'targetSelection') ?? '').split(',')[0]!)) !== strtoupper(trim(selection.split(',')[0]!)))
        return null;
    const g = d.ft_painted_wall_geometry(at(wire, 'geometry')), a = ftPaintedWallAttachment(strval(d.item_field(ability, 'item_id')));
    if (!truthy(g) || !a || at(g, 'attachment') !== a || empty(d.layer_value(ability, 'persistent_aoe')) || !empty(d.layer_value(ability, 'aoe_follows_target')) || num(d.ft_painted_wall_length(g)) > num(d.layer_value(ability, 'aoe_distance')) + .01 || Math.abs(num(at(g, 'heightFt')) - num(d.layer_value(ability, 'aoe_distance_height'))) > .01 || Math.abs(num(at(g, 'thicknessFt')) - num(d.layer_value(ability, 'aoe_distance_depth'))) > .01)
        d.janus_validation_veto('Invalid painted wall. Paint the area again.');
    const token = trim(selection.split(',')[0]!);
    if (!d.is_target_encounter_format(token))
        d.janus_validation_veto('A painted wall needs an encounter placement.');
    const anchors = d.parse_encounter_target_string(token), points = values(at(g, 'points'));
    for (const [end, p] of [['origin', points[0]], ['distance', points[points.length - 1]]] as const)
        for (const axis of ['x', 'y', 'z']) {
            const value = at(anchors, 'status_' + end + '_point_' + axis);
            if (!is_numeric(value) || Math.abs(num(value) - num(at(p, axis))) > .001)
                d.janus_validation_veto('The painted wall placement changed. Paint it again.');
        }
    return g;
}
